import React, { FC } from "react";
import Link from "../Link";
import ExternalLink from "../ExternalLink";

import RightArrow from "../../img/right-arrow.svg";

import { 
  ImageTextV2Props,
  SizeKey,
  ImageCustomWidths
} from "./types";

import {
  Container,
  TextContainer,
  ImageContainer,
  Button,
  HighlightWord,
  ButtonsSection,
  Paragraph,
  Title,
  TextButtonContainer,
  DescriptionsContainer,
  ButtonGroupContainer
} from "./styles";

interface ButtonLinkProps {
  link?: string;
  isExternal?: boolean;
  children?: React.ReactNode;
  href?: string;
}

const ImageTextV2:FC<ImageTextV2Props> = ({
  imageProps,
  buttonProps,
  hasBorder,
  textProps,
  customTextAlign,
  customDirection,
  sectionType,
  lastSection,
  alignContent,
  buttonGroupProps
}) => {
    const ButtonLink: FC<ButtonLinkProps> = ({ children, link, isExternal = false }) => {
      return isExternal ? (
        <ExternalLink href={link}>{children}</ExternalLink>
      ) : (
        <Link to={link}>{children}</Link>
      );
  };

  const _getImageCustomWidths = (): ImageCustomWidths => {
    const imageWidths: ImageCustomWidths = {};

     if (imageProps?.imageCustomWidths) {
      Object.entries(imageProps.imageCustomWidths).forEach(([size, config]) => {
        const sizeKey = size as SizeKey;
        
        imageWidths[`imageWidth${sizeKey}`] = config.width;
        imageWidths[`imageAspectRatio${sizeKey}`] = config.aspectRatio;
      });
    }

    return imageWidths;
  };
  
  return (
    <>
        <Container 
            imagePositionFixed={imageProps?.imagePositionFixed || false}
            imagePosition={imageProps?.imagePosition || "right"} 
            alignContent={alignContent}
            direction={customDirection}
            imagePositionMobileBreakpoint={imageProps?.imagePositionMobile?.breakpoint}
            imagePositionMobile={imageProps?.imagePositionMobile?.position}
            sectionType={sectionType} 
            lastSection={lastSection}
            hasBorder={hasBorder}
            >
          <TextButtonContainer
              customTextAlign={customTextAlign || "left"}
              customDirection={customDirection || "row"}
              customTextWidth={textProps?.customTextWidth}
              imagePositionFixed={imageProps?.imagePositionFixed}
              imagePositionMobileBreakpoint={imageProps?.imagePositionMobile?.breakpoint}
              imagePositionMobile={imageProps?.imagePositionMobile?.position}
              hasBorder={hasBorder}
            >
            <TextContainer>
            <Title
                customTitleColor={textProps?.customTitleColor}
                customTitleFontSize={textProps?.customTitleFontSize}
                customTitleLineHeight={textProps?.customTitleLineHeight}
                sectionType={sectionType}
                customTitleFontSizeMobile={textProps?.customTitleFontSizeMobile}
                titleMaxWidth={textProps?.titleMaxWidth}
                titleWidth={textProps?.titleWidth}
                customTitleLetterSpacing={textProps?.customTitleLetterSpacing}
                >
                  {textProps?.title}
                  {textProps?.highlightWordBreak && <br/>}
                  {textProps?.highlightWord && (
                    <HighlightWord 
                        highlightWordColor={textProps?.highlightWordColor}
                    >{textProps?.highlightWord}</HighlightWord>
                  )}
            </Title>
            {textProps?.descriptions &&
              <DescriptionsContainer>
                {textProps?.descriptions.map((description) => (
                  <Paragraph 
                        id={description.text}
                        maxWidth={description?.maxWidth}
                        customFontSize={description?.customFontSize}
                        customFontFamily={description?.customFontFamily}
                        sectionType={sectionType}
                        type={description?.type}
                    >
                    {description.text}
                  </Paragraph>
                ))}
              </DescriptionsContainer>
            }
          </TextContainer>
          {buttonProps?.link && (
            <ButtonsSection>
              {buttonProps?.label && (
                  <ButtonLink link={buttonProps.link} isExternal={buttonProps?.isExternal}>
                    <Button 
                        buttonType={buttonProps?.type || "primary"}>
                      {buttonProps?.label}
                      <RightArrow />
                    </Button>
                  </ButtonLink>
                )}
              </ButtonsSection>
            )}
             {
              buttonGroupProps && buttonGroupProps.buttons && (
                <ButtonGroupContainer>
                  {buttonGroupProps.buttons.map((button) => (
                    <ButtonsSection>
                      <ButtonLink 
                        link={button?.link} 
                        isExternal={button?.isExternal}>
                        <Button
                          buttonType={button?.type || "primary"}>
                          {button.label}
                        </Button>
                      </ButtonLink>
                    </ButtonsSection>
                  ))}
                </ButtonGroupContainer>
              )
            }
            </TextButtonContainer>
            {
              imageProps?.imageSrc && 
                <ImageContainer
                    {..._getImageCustomWidths()}
                    scaleImageOnMobile={imageProps.scaleImageOnMobile}
                    imagePositionFixed={imageProps.imagePositionFixed}
                    imageCustomWidth={imageProps.imageCustomWidth}
                    imageCustomHeight={imageProps.imageCustomHeight}
                    imagePosition={imageProps.imagePosition || "right" }
                    imageContainerMaxWidth={imageProps.imageContainerMaxWidth || "50%"}
                    imageOffsetOnMobile={imageProps.imageOffsetOnMobile}
                    >
                    <img 
                    src={imageProps.imageSrc} 
                    alt={imageProps.imageAlt || "Section image"} 
                    />
                </ImageContainer> 
            }
        </Container>
    </>
  );
}

export default ImageTextV2;
